import React from 'react';
import { number, string } from 'prop-types';
import { useCabinetVisualizerStore } from '../../../../store';
import {
  selectDesignProducts,
  getSegments,
  DEFAULT_TOUCH_POINT_WIDTH
} from '../../../../store/cabinetVisualizer/selectors';
import {
  cabinetToBoundingBox,
  getConstrainedWallHeight,
  getTotalBounding,
  segmentsToBoundingBox
} from '../utils';

import { DimensionArrow } from './DimensionArrow';
import { inchesToPxs } from '../../../../utils/wall-size-utils';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';

export const DimensionArrowsContainer = ({
  designId,
  rows,
  columns,
  pxInInches,
  zoom,
  anchorSide,
  floorOffset
}) => {

  const { cabinetVisualizer } = useCabinetVisualizerStore();

  const products = (designId && cabinetVisualizer) ? selectDesignProducts(cabinetVisualizer, designId) : [];

  const zoomPx = zoom * pxInInches;
  const arrowInchesToPxs = (value) => inchesToPxs(value, zoomPx);
  const isMobile = useIsMobileDevice();

  // All the info the the currently selected cabinet to add
  const segments = segmentsToBoundingBox(getSegments(columns, getConstrainedWallHeight(rows)));
  const cabinetBoundings = products?.map((cab) => cabinetToBoundingBox(cab));
  const totalBounds = getTotalBounding(cabinetBoundings);

  function getCabinetBoundingsBySide() {
    return {
      left: cabinetBoundings.filter((curCab) => curCab.left === totalBounds.left),
      right: cabinetBoundings.filter((curCab) => curCab.right === totalBounds.right),
      top: cabinetBoundings.filter((curCab) => curCab.top <= segments[1].top),
      bottom: cabinetBoundings.filter((curCab) => curCab.bottom === segments[2].bottom)
    };
  }
  const cabinetsBySide = getCabinetBoundingsBySide();

  function calculateArrows() {
    const padding = isMobile ? 5 : 3;
    const paddingLeft = padding + 2;
    const paddingRight = padding + 2;
    const size = 2;

    const hasTopCabinets = cabinetsBySide.top?.length > 0;
    const hasBottomCabinets = cabinetsBySide.bottom?.length > 0;

    // Calculate the wall segments total height
    let effectiveHeight;
    const wallSegmentsHeight = segments[segments.length - 1].bottom - segments[0].top;

    // Always use wall segments height for positioning, not totalBounds.height
    effectiveHeight = wallSegmentsHeight;

    const topYPosition = -padding - floorOffset - effectiveHeight + totalBounds.top;

    // For vertical arrows, position them at the top of the respective cabinet group
    const topCabinetTop = hasTopCabinets ? Math.min(...cabinetsBySide.top.map((cabinet) => cabinet.top)) : 0;
    const bottomCabinetTop = hasBottomCabinets ? Math.min(...cabinetsBySide.bottom.map((cabinet) => cabinet.top)) : 0;

    const leftArrowYPosition = hasTopCabinets
      ? -padding - floorOffset - effectiveHeight + topCabinetTop + 4
      : -padding - floorOffset - effectiveHeight + bottomCabinetTop + 4;

    let topPosX = 0;
    let bottomPosX = 0;
    let topLength = 0;
    let bottomLength = 0;

    // Move the top and bottom arrows to the correct x position
    if (anchorSide === 'right') {
      topLength = totalBounds.right - Math.min(...cabinetsBySide.top?.map((cur) => cur?.left));
      bottomLength = totalBounds.right - Math.min(...cabinetsBySide.bottom?.map((cur) => cur?.left));

      topPosX = -topLength - DEFAULT_TOUCH_POINT_WIDTH; // TODO: REMOVE THIS TOUCHPOINT PADDING
      bottomPosX = -bottomLength - DEFAULT_TOUCH_POINT_WIDTH; // TODO: REMOVE THIS TOUCHPOINT PADDING
    } else {
      topPosX = totalBounds.left;
      bottomPosX = totalBounds.left;

      topLength = Math.max(...cabinetsBySide.top?.map((cur) => cur?.right)) - totalBounds.left;
      bottomLength = Math.max(...cabinetsBySide.bottom?.map((cur) => cur?.right)) - totalBounds.left;
    }

    const result = {
      left: {
        posX: totalBounds.left - paddingLeft,
        posY: leftArrowYPosition,
        type: 'vertical',
        size,
        length: totalBounds.bottom - totalBounds.top,
        key: 'left'
      },
      right: {
        posX: anchorSide === 'right'
          ? -(segments[0].right - totalBounds.right) + paddingRight
          : totalBounds.right + paddingRight,
        posY: leftArrowYPosition,
        type: 'vertical',
        size,
        length: totalBounds.bottom - totalBounds.top,
        key: 'right'
      },
      top: {
        posX: topPosX,
        posY: topYPosition,
        type: 'horizontal',
        size,
        length: topLength,
        key: 'top'
      },
      bottom: {
        posX: bottomPosX,
        posY: padding - floorOffset,
        type: 'horizontal',
        size,
        length: bottomLength,
        key: 'bottom'
      },
    };

    return result;
  }

  let arrowsArray = [];

  if (cabinetBoundings.length > 0) {
    const arrows = calculateArrows();
    arrowsArray = [];
    if (arrows.top.length > 0) {
      arrowsArray.push(arrows.top);
    }

    if (arrows.bottom.length > 0) {
      arrowsArray.push(arrows.bottom);
    }
    arrowsArray.push(arrows[anchorSide]);

  }

  return (
    <div className="dimension-arrows-container">
      {
        arrowsArray.map(({
          posX, posY, size, length, type, key
        }) => (
          <DimensionArrow
            key={key}
            posX={posX}
            posY={posY}
            size={size}
            length={length}
            type={type}
            scaleFn={arrowInchesToPxs}
          />
        ))
      }
    </div>
  );
};

DimensionArrowsContainer.propTypes = {
  designId: string.isRequired,
  rows: number,
  columns: number,
  anchorSide: string,
  pxInInches: number.isRequired,
  zoom: number,
  floorOffset: number,
};

DimensionArrowsContainer.defaultProps = {
  rows: 0,
  columns: 0,
  anchorSide: 'left',
  zoom: 1,
  floorOffset: 0
};
